import { useRef, useState, useCallback, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import _ from "lodash";
import {
    IProductDetail,
    IBookingAvailability,
    IPriceGroup,
    AvailabilityType,
    IAvailabilityByRange,
    IAvailabilityByProductAndRange,
} from "@interfaces";
import { IProductPricesDetails } from "src/Interfaces/cart";
import { openToast, useAppSelector, useAppDispatch, selectAuth, selectAccount } from "@redux";
import {
    createNewQuoteAndReload,
    loadQuotes,
    addItemToExistingQuoteAndReload,
} from "src/Redux/Slices/allQuotes";
import { cartApi, toursApi } from "@api";
import { addToCartAndFetch } from "../checkout";
import { PATH_CART, PATH_QUOTES } from "@routes";
import { ClipLoaderCommon } from "@components";
import Tooltip from "@mui/material/Tooltip";
import { theme, sessionHelper } from "@utilities";
import { lsJwt } from "src/Utilities/storage";
// Imported by module path, not via the @utilities barrel: the panel's test suites
// replace that barrel wholesale with a small stub, so a barrel import lands here as
// undefined at runtime under Jest.
import { findNextAvailableDate } from "src/Utilities/nextAvailableDate";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { SuccessToast } from "src/Components/toasts/SuccessToast";
import { SignInBottomSheetModal } from "./SignInBottomSheetModal";
import { shouldPromptSignup } from "../signupPromptGate";
import { stashGuestCartItem } from "../guestCartStash";
import {
    clearPendingSocialAction,
    consumeAddedToCartAfterSignIn,
    storePendingSocialAction,
} from "../pendingSocialAction";
import { isDuplicateCartItemError } from "../cartErrors";
import { RedirectToCart } from "./RedirectToCartPopUp";
import { createNewQuoteModal, addToExistingQuoteModal } from "src/Module/Modals";
import {
    getAllFaresAvailabilities,
    FareGroupAvailabilityResult,
    faresPricesGroups,
    getRRPOfAvailability,
    isExcludedFromSpaceCount,
    resolveBookingDays,
    anchorIndexToStartDate,
} from "src/Utilities/cartHelper";
import { pushSelectItem, pushAddToCart } from "@utilities";
import { DURATION_TIME, SIGN_IN_PROMPT_SEEN_KEY } from "@constants";
import { useCalendarRangeSelection } from "./useCalendarRangeSelection";

import {
    AvailabilityLevel,
    getAvailabilityLevel,
    AVAILABILITY_COLORS,
    formatAvailableSpaces,
    formatAvailableSpacesLabel,
} from "./availabilityColorHelpers";
import {
    DepartureTimeOption,
    FareDepartureTimesState,
    fareNeedsTimeSelection as fareNeedsTimeSelectionFn,
    fareTimeKey as fareTimeKeyFn,
    parseDepartureTimeFields,
    filterAvailableDepartureTimes,
    deriveDepartureTimesFromRows,
    deriveDepartureTimesFromAvailabilityRows,
    pendingBookingTimeGroups,
    buildBookingTimeCatalog,
    collapseTimedFareRows,
    mergeDisplayRows,
    findTimeAvailRow,
    mergeFareGroupBookingTimes,
} from "./departureTimeHelpers";

interface DisplayRow {
    date: string;
    fareGroupId: string;
    fareGroupName: string;
    bookingTimeId: string;
    bookingTimeName: string;
    numAvailable: number;
    availability: IBookingAvailability;
    availIndex: number;
    priceGroup: IPriceGroup;
    productPricesDetailsId: string | number;
    numPax: number;
}

interface SelectedItem extends DisplayRow {
    quantity: number;
}

interface Props {
    product: IProductDetail;
    /** Called when user clicks "Check Availability" (for GTM select_item). */
    onCheckAvailabilityClick?: () => void;
    /**
     * When false, defers the automatic availability fetch until the full product
     * (all fare groups) has hydrated. The product page loads progressively
     * (homepage store -> FT cache -> TDMS), so fetching too early queries a
     * partial fare set and the calendar settles on incomplete availability.
     * Defaults to true for callers that already hold a complete product.
     */
    isProductFullyLoaded?: boolean;
    initialDate?: Date | null;
    /**
     * Pre-selects a date range and opens the calendar at its start month.
     * Takes precedence over `initialDate` for range setup. Useful when this
     * panel is reused in a modal scoped to an existing date window (e.g. a
     * trip planner trip range).
     */
    initialDateRange?: { start: Date; end: Date } | null;
    /**
     * "page" (default) keeps the breakout layout used on the product page
     * (90vw, viewport-centered). "modal" disables the breakout so the panel
     * fits inside a constrained container like a Dialog.
     */
    layout?: "page" | "modal";
    /**
     * "cart" (default) keeps every existing behaviour: Add to Cart, Book Now,
     * Save to Quote, GTM events, sign-in prompt, etc. "trip" replaces the
     * single CTA with "Add to Trip" and routes the selections through
     * `onAddToTrip` instead of touching the cart. Used by the Trip Planner
     * availability popup so picking a date adds the item to the planner
     * rather than the live shopping cart.
     */
    actionMode?: "cart" | "trip";
    /**
     * Required when `actionMode === "trip"`. Called once per selected fare
     * row when the user clicks Add to Trip. The selection includes the
     * date in `DD-MMM-YYYY` form (matching the format used elsewhere in
     * this panel) so callers can map it onto a trip day index themselves.
     */
    onAddToTrip?: (selection: TripPanelSelection) => void;
}

export interface TripPanelSelection {
    date: string;
    fareTypeId: string | number;
    fareName: string;
    productPricesDetailsId: string | number;
    bookingTimeId: string | null;
    bookingTimeName: string;
    quantity: number;
    numPax: number;
    rrp: number;
    /**
     * Index of the matching row in the checkAvailRange response. Must be
     * forwarded into the trip item so `tripItemToAddPayload` can send the
     * correct `selectedAvailableIndices` — otherwise the Save Trip / Add
     * to Cart calls hit a 400 "Validation Error" when the response isn't
     * sorted with the picked date at row 0.
     */
    availIndex: number;
}

const getProductPricesDetailsIdFromAvail = (avail: IBookingAvailability): string | number => {
    if (avail.availabilityType === AvailabilityType.ByRange) {
        return (avail as IAvailabilityByRange).productPricesDetailsId;
    }
    return (avail as IAvailabilityByProductAndRange).FarePrice?.productPricesDetailsId ?? "";
};

const buildDisplayRows = (results: FareGroupAvailabilityResult[]): DisplayRow[] => {
    const rows: DisplayRow[] = [];

    for (const { priceGroup, bookingTimes, availabilities } of results) {
        const numPax =
            parseFloat(String(priceGroup.prices[0]?.numPax ?? 1).replace(/[^\d.-]/g, "")) || 1;

        const best: Record<string, { avail: IBookingAvailability; idx: number }> = {};
        availabilities.forEach((avail, idx) => {
            if (avail.NumAvailable <= 0) return;
            const btId = String(avail.BookingTimeID ?? "0");
            const dedupeKey = `${avail.BookingDate}|${btId}`;
            if (!best[dedupeKey] || avail.NumAvailable > best[dedupeKey].avail.NumAvailable) {
                best[dedupeKey] = { avail, idx };
            }
        });

        Object.values(best).forEach(({ avail, idx }) => {
            const btId = String(avail.BookingTimeID ?? "0");
            const matchedName = bookingTimes.find((bt) => String(bt.id) === btId)?.name;
            const btName =
                matchedName ??
                (avail.BookingTime && avail.BookingTime !== "Daily availability"
                    ? avail.BookingTime
                    : "") ??
                "";
            const isTimeString = !btName && btId !== "0" && /^\d{2}:\d{2}/.test(btId);
            const displayTimeName = btName || (isTimeString ? btId.substring(0, 5) : "");

            rows.push({
                date: avail.BookingDate,
                fareGroupId: priceGroup.id,
                fareGroupName: priceGroup.name,
                bookingTimeId: btId,
                bookingTimeName: displayTimeName,
                numAvailable: avail.NumAvailable,
                availability: avail,
                availIndex: idx,
                priceGroup,
                productPricesDetailsId: getProductPricesDetailsIdFromAvail(avail),
                numPax,
            });
        });
    }

    return rows;
};

const collectBookedOutDates = (results: FareGroupAvailabilityResult[]): Set<string> => {
    const allDates = new Set<string>();
    const availableDates = new Set<string>();

    for (const { availabilities } of results) {
        availabilities.forEach((avail) => {
            allDates.add(avail.BookingDate);
            if (avail.NumAvailable > 0) {
                availableDates.add(avail.BookingDate);
            }
        });
    }

    const bookedOut = new Set<string>();
    allDates.forEach((date) => {
        if (!availableDates.has(date)) {
            bookedOut.add(date);
        }
    });
    return bookedOut;
};

const parseBookingTime = (timeName: string): number => {
    if (!timeName) return 0;
    const match = timeName.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return 0;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3].toUpperCase();
    if (period === "AM" && hours === 12) hours = 0;
    if (period === "PM" && hours !== 12) hours += 12;
    return hours * 60 + minutes;
};

const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export const AllFaresAvailabilityPanel = ({
    product,
    onCheckAvailabilityClick,
    isProductFullyLoaded = true,
    initialDate,
    initialDateRange,
    layout = "page",
    actionMode = "cart",
    onAddToTrip,
}: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { jwt } = useAppSelector(selectAuth);
    const isLoggedIn = !_.isNil(jwt);
    const { metadata } = useAppSelector(selectAccount);
    const isAgent = !!(metadata?.is_commission_agent || metadata?.is_points_agent);
    const showSaveToQuote = isLoggedIn && isAgent;
    const [isSavingToQuote, setIsSavingToQuote] = useState(false);
    const [isQuoteDropdownOpen, setIsQuoteDropdownOpen] = useState(false);

    const [displayRows, setDisplayRows] = useState<DisplayRow[]>([]);
    const [bookedOutDates, setBookedOutDates] = useState<Set<string>>(new Set());
    const [selections, setSelections] = useState<SelectedItem[]>([]);
    const [isSubmitInProgress, setIsSubmitInProgress] = useState<null | "cart" | "book-now">(null);
    /** Raised after a successful Add to Cart, offering the cart or the home page. */
    const [showAddedToCart, setShowAddedToCart] = useState(false);
    const [showSignInPrompt, setShowSignInPrompt] = useState(false);
    const [cartHasItems, setCartHasItems] = useState(false);
    const [expandedDate, setExpandedDate] = useState<string | null>(null);
    const [expandedFareKey, setExpandedFareKey] = useState<string | null>(null);
    const [fareDepartureTimes, setFareDepartureTimes] = useState<
        Record<string, FareDepartureTimesState>
    >({});
    const [calendarMonth, setCalendarMonth] = useState(() => {
        if (initialDateRange?.start) return moment(initialDateRange.start).startOf("month");
        return initialDate ? moment(initialDate).startOf("month") : moment().startOf("month");
    });
    const [{ rangeStart, rangeEnd }, rangeActions] = useCalendarRangeSelection();
    const [selectedBookingTimeId, setSelectedBookingTimeId] = useState<string | undefined>(
        undefined,
    );
    const [bookingTimesLoaded, setBookingTimesLoaded] = useState(false);
    // Per-fare booking-time catalog (id + name), populated on bootstrap. Used to
    // decide whether a fare offers multiple departure times (so it gets the
    // per-date time accordion, consistently across booking systems) and to fetch
    // each time's availability when the accordion is opened.
    const [fareGroupBookingTimes, setFareGroupBookingTimes] = useState<
        Record<string, Array<{ id: string; name: string }>>
    >({});
    // Fare selection was removed: the calendar now fetches every fare in a
    // single `checkAvailabilityProduct` call and shows per-fare counts when a
    // date is expanded, so there's no need to pre-select a fare. Pinned to
    // "all" so the existing all-fares code paths (filteredPriceGroups) keep
    // working unchanged.
    const selectedFareGroupId = "all";

    const setRangeToMonth = useCallback(
        (monthMoment: moment.Moment) => {
            const today = moment();
            const monthStart = monthMoment.clone().startOf("month");
            const monthEnd = monthMoment.clone().endOf("month");
            const effectiveStart = today.isAfter(monthStart)
                ? today.format("YYYY-MM-DD")
                : monthStart.format("YYYY-MM-DD");
            rangeActions.setRangeBoth(effectiveStart, monthEnd.format("YYYY-MM-DD"));
        },
        [rangeActions],
    );

    const defaultRangeAppliedRef = useRef(false);
    useEffect(() => {
        if (defaultRangeAppliedRef.current) return;
        defaultRangeAppliedRef.current = true;
        if (initialDateRange?.start && initialDateRange?.end) {
            // Pre-seed the date range from the caller (e.g. a trip planner
            // trip window) instead of defaulting to the current month.
            rangeActions.setRangeBoth(
                moment(initialDateRange.start).format("YYYY-MM-DD"),
                moment(initialDateRange.end).format("YYYY-MM-DD"),
            );
        } else {
            setRangeToMonth(calendarMonth);
        }
    }, []);
    const pendingActionRef = useRef<{ mode: "addToCart" | "bookNow" } | null>(null);
    const autoFetchTriggeredRef = useRef(false);
    const pendingAutoExpandDateRef = useRef<string | null>(null);
    const fareDetailRef = useRef<HTMLDivElement>(null);
    const shouldScrollToFaresRef = useRef(false);
    // Set when the page loads without an explicit date, so the calendar can open on
    // the soonest bookable day once the preloaded months have arrived.
    const pendingFirstAvailableRef = useRef(false);
    const loadingCountRef = useRef(0);
    const [isLoading, setIsLoading] = useState(false);
    /**
     * True from mount until the auto-fetch has its first month under way.
     *
     * `isLoading` only covers a request already in flight, and the auto-fetch
     * waits on the booking-time catalog and on the product's full price load. The
     * calendar therefore rendered as an ordinary empty month first and only greyed
     * out once the fares arrived, which read as the page finishing and then
     * stalling. Seeded for the cases the auto-fetch effect itself acts on.
     */
    const [isAwaitingFirstFetch, setIsAwaitingFirstFetch] = useState(
        () => (layout === "page" && actionMode === "cart") || !!initialDate,
    );
    const bookingTimesFetchedIdsRef = useRef<Set<string>>(new Set());
    const fetchedMonthsRef = useRef<Set<string>>(new Set());
    const [fetchedMonthsSet, setFetchedMonthsSet] = useState<Set<string>>(new Set());

    const syncFetchedMonthsState = useCallback(() => {
        setFetchedMonthsSet(new Set(fetchedMonthsRef.current));
    }, []);

    const hasFetchedAnyMonth = fetchedMonthsSet.size > 0;

    const priceGroups = useMemo(
        () => faresPricesGroups(product.faresPrices || []),
        [product.faresPrices],
    );

    const filteredPriceGroups = useMemo(() => {
        if (selectedFareGroupId === "all") return priceGroups;
        return priceGroups.filter((pg) => pg.id === selectedFareGroupId);
    }, [priceGroups, selectedFareGroupId]);

    useEffect(() => {
        if (priceGroups.length === 0) return;
        const shouldCheckInGroup =
            product.apiProviderId > 0 && product.groupFaresForAvailabilityCheck;
        if (shouldCheckInGroup) {
            if (!bookingTimesLoaded) setBookingTimesLoaded(true);
            return;
        }

        // The product detail page hydrates progressively (homepage -> FT cache ->
        // TDMS), so the fare groups available on the first render may differ from
        // the final set. Fetch the booking-time catalog for any group we have not
        // resolved yet, rather than latching after a single pass, so multi-time
        // fares show the departure-time accordion once full fare data arrives.
        const pendingGroups = pendingBookingTimeGroups(
            priceGroups,
            bookingTimesFetchedIdsRef.current,
        );
        if (pendingGroups.length === 0) {
            if (!bookingTimesLoaded) setBookingTimesLoaded(true);
            return;
        }

        const allSeenIds = new Set<string>();
        const allTimes: Array<{ id: string; name: string }> = [];
        const perGroupMap: Record<string, Array<{ id: string; name: string }>> = {};

        Promise.allSettled(
            pendingGroups.map(async (priceGroup): Promise<{ id: string; resolved: boolean }> => {
                const embeddedTimes = priceGroup.prices.flatMap(
                    (price) => price.bookingTimes ?? [],
                );

                // The Generic availability response only carries a single, arbitrary
                // booking time per fare, so the authoritative full departure-time
                // catalog must come from booking detail. Fetch it (union with any
                // embedded times) and treat the group as resolved only on success so
                // a transient failure can retry on a later render.
                let detailTimes: typeof embeddedTimes = [];
                let resolved = false;
                const pricedFare = priceGroup.prices.find(
                    (p) => !_.isNil(p.productPricesDetailsId),
                );
                if (pricedFare) {
                    try {
                        const bookingDetailResp = await toursApi.getBookingDetail(
                            pricedFare.productPricesDetailsId,
                        );
                        const bts = bookingDetailResp.data.bookingTimes ?? [];
                        const isDaily =
                            bts.length === 1 && bts[0].BookingTime === "Daily availability";
                        detailTimes = isDaily ? [] : bts;
                        resolved = true;
                    } catch {
                        /* fall back to embedded times below */
                    }
                }

                const bookingTimes = buildBookingTimeCatalog(detailTimes, embeddedTimes);
                perGroupMap[priceGroup.id] = bookingTimes;
                bookingTimes.forEach((bt) => {
                    if (!allSeenIds.has(bt.id)) {
                        allSeenIds.add(bt.id);
                        allTimes.push(bt);
                    }
                });

                return { id: priceGroup.id, resolved };
            }),
        ).then((results) => {
            results.forEach((r) => {
                if (r.status === "fulfilled" && r.value.resolved) {
                    bookingTimesFetchedIdsRef.current.add(r.value.id);
                }
            });
            setFareGroupBookingTimes((prev) => ({ ...prev, ...perGroupMap }));
            setBookingTimesLoaded(true);
            if (allTimes.length === 1) {
                setSelectedBookingTimeId(allTimes[0].id);
            }
        });
    }, [
        priceGroups,
        bookingTimesLoaded,
        product.apiProviderId,
        product.groupFaresForAvailabilityCheck,
    ]);

    const runFetchForMonth = useCallback(
        (
            monthMoment: moment.Moment,
            rangeBounds?: { rangeStart: moment.Moment; rangeEnd: moment.Moment },
        ) => {
            const monthKey = monthMoment.format("YYYY-MM");
            if (fetchedMonthsRef.current.has(monthKey)) return;
            if (filteredPriceGroups.length === 0) return;

            const today = moment().startOf("day");
            const monthStart = monthMoment.clone().startOf("month");
            const monthEnd = monthMoment.clone().endOf("month");

            let effectiveStart: moment.Moment;
            let effectiveEnd: moment.Moment;

            if (rangeBounds) {
                effectiveStart = moment.max(
                    monthStart,
                    rangeBounds.rangeStart.clone().startOf("day"),
                    today,
                );
                effectiveEnd = moment.min(monthEnd, rangeBounds.rangeEnd.clone().endOf("day"));
            } else {
                effectiveStart = monthStart.isBefore(today) ? today : monthStart;
                effectiveEnd = monthEnd;
            }

            const numDays = effectiveEnd.diff(effectiveStart, "days") + 1;

            if (numDays <= 0) return;

            fetchedMonthsRef.current.add(monthKey);
            syncFetchedMonthsState();
            loadingCountRef.current += 1;
            setIsLoading(true);

            const TIMEOUT_MS = 45000;
            const timeoutPromise = new Promise<never>((_, reject) =>
                setTimeout(
                    () =>
                        reject(
                            new Error(`Availability fetch timed out after ${TIMEOUT_MS / 1000}s`),
                        ),
                    TIMEOUT_MS,
                ),
            );

            Promise.race([
                getAllFaresAvailabilities({
                    priceGroups: filteredPriceGroups,
                    startDate: effectiveStart.toDate(),
                    numDays,
                    productId: product.productId,
                    apiProviderId: product.apiProviderId,
                    groupFaresForAvailabilityCheck: product.groupFaresForAvailabilityCheck,
                    selectedBookingTimeId,
                    // The product fed to this panel is always an IProductDetail from
                    // getProductHelper, whose productClass carries the TDMS detail
                    // class CODE ("A" = accommodation), NOT the search/homepage `n`
                    // vocabulary (the full word "accommodation"). Matching only the
                    // full word meant real accommodation products never took the
                    // accommodation availability path (they fell through to
                    // checkAvailabilityProduct). Accept both vocabularies so the
                    // accommodation branch fires regardless of the data source.
                    isAccommodation:
                        product.productClass === "A" || product.productClass === "accommodation",
                }),
                timeoutPromise,
            ])
                .then((results) => {
                    const rows = buildDisplayRows(results);
                    const newBookedOut = collectBookedOutDates(results);
                    setDisplayRows((prev) => mergeDisplayRows(prev, rows));
                    // Response-derived (orphan) fare groups are absent from the
                    // bootstrap booking-time catalog, so register their times here
                    // or their multi-time fares render once per departure time.
                    setFareGroupBookingTimes((prev) => mergeFareGroupBookingTimes(prev, results));
                    setBookedOutDates((prev) => {
                        const merged = new Set(prev);
                        newBookedOut.forEach((d) => merged.add(d));
                        return merged;
                    });
                    if (pendingAutoExpandDateRef.current) {
                        const dateToExpand = pendingAutoExpandDateRef.current;
                        pendingAutoExpandDateRef.current = null;
                        const hasRows = rows.some(
                            (r) =>
                                moment(r.date, "DD-MMM-YYYY").format("YYYY-MM-DD") === dateToExpand,
                        );
                        if (hasRows) {
                            setExpandedDate(dateToExpand);
                        }
                    }
                })
                .catch((err) => {
                    fetchedMonthsRef.current.delete(monthKey);
                    syncFetchedMonthsState();
                    dispatch(
                        openToast({
                            type: "error",
                            autoHideDuration: DURATION_TIME,
                            message: err?.message?.includes("timed out")
                                ? "Availability check timed out. Please try again."
                                : "Failed to load availability. Please try again.",
                        }),
                    );
                })
                .finally(() => {
                    loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
                    if (loadingCountRef.current === 0) setIsLoading(false);
                });
        },
        [
            product.productId,
            product.apiProviderId,
            product.groupFaresForAvailabilityCheck,
            product.productClass,
            dispatch,
            filteredPriceGroups,
            selectedBookingTimeId,
        ],
    );

    useEffect(() => {
        const isProductPage = layout === "page" && actionMode === "cart";
        const isProductLanding = isProductPage && !initialDate;
        if ((!isProductPage && !initialDate) || autoFetchTriggeredRef.current) return;
        if (!isProductFullyLoaded) return;
        // Checked before `bookingTimesLoaded`, which it governs: a product with no
        // price groups never loads booking times, so testing them first put this
        // behind a gate that could not open and left the grid greyed out for good.
        if (priceGroups.length === 0) {
            setIsAwaitingFirstFetch(false);
            return;
        }
        if (!bookingTimesLoaded) return;
        if (!rangeStart || !rangeEnd) return;
        autoFetchTriggeredRef.current = true;
        setIsAwaitingFirstFetch(false);
        pendingAutoExpandDateRef.current = initialDate
            ? moment(initialDate).format("YYYY-MM-DD")
            : null;
        pendingFirstAvailableRef.current = isProductLanding;
        console.log(
            isProductLanding
                ? "[AllFaresAvail] Auto-fetching three months of availability"
                : "[AllFaresAvail] Auto-fetching availability for initial date",
        );
        onCheckAvailabilityClick?.();
        fetchedMonthsRef.current.clear();
        syncFetchedMonthsState();
        const monthsToFetch = isProductLanding ? 3 : 1;
        for (let monthOffset = 0; monthOffset < monthsToFetch; monthOffset += 1) {
            const month = calendarMonth.clone().add(monthOffset, "month");
            const start = monthOffset === 0 ? moment(rangeStart) : month.clone().startOf("month");
            const end = monthOffset === 0 ? moment(rangeEnd) : month.clone().endOf("month");
            runFetchForMonth(month, { rangeStart: start, rangeEnd: end });
        }
    }, [
        actionMode,
        layout,
        initialDate,
        bookingTimesLoaded,
        isProductFullyLoaded,
        priceGroups,
        rangeStart,
        rangeEnd,
        calendarMonth,
        runFetchForMonth,
        onCheckAvailabilityClick,
        syncFetchedMonthsState,
    ]);

    useEffect(() => {
        if (!shouldScrollToFaresRef.current || !expandedDate) return;
        shouldScrollToFaresRef.current = false;
        // Deferred so the panel has rendered and laid out; centred so it reads as
        // "here are the fares" without the header covering the date heading.
        const timeoutId = setTimeout(() => {
            fareDetailRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        return () => clearTimeout(timeoutId);
    }, [expandedDate]);

    const byDate = _.groupBy(displayRows, (r) => r.date);

    const byDateNormalised: Record<string, DisplayRow[]> = {};
    Object.keys(byDate).forEach((key) => {
        const normalised = moment(key, "DD-MMM-YYYY").format("YYYY-MM-DD");
        if (!byDateNormalised[normalised]) {
            byDateNormalised[normalised] = [];
        }
        byDateNormalised[normalised].push(...byDate[key]);
    });

    const bookedOutNormalised = useMemo(() => {
        const set = new Set<string>();
        bookedOutDates.forEach((d) => {
            set.add(moment(d, "DD-MMM-YYYY").format("YYYY-MM-DD"));
        });
        return set;
    }, [bookedOutDates]);

    /**
     * Default the calendar to the next available day when no date was requested.
     *
     * Waits for `isLoading` to clear rather than acting on the first fetch to
     * resolve: the three preloaded months are fetched in parallel and settle in any
     * order, so choosing early would open on a later month's date while an earlier
     * one was still in flight. The flag is cleared once the load settles even if
     * nothing was found, so this stays a load-time default and never re-fires over
     * a month the visitor navigated to themselves.
     */
    useEffect(() => {
        if (!pendingFirstAvailableRef.current || isLoading || !hasFetchedAnyMonth) return;
        pendingFirstAvailableRef.current = false;
        const nextAvailable = findNextAvailableDate(
            Object.keys(byDateNormalised),
            moment().startOf("day").format("YYYY-MM-DD"),
        );
        if (!nextAvailable) return;
        setExpandedDate(nextAvailable);
        const nextMonth = moment(nextAvailable, "YYYY-MM-DD").startOf("month");
        if (!nextMonth.isSame(calendarMonth, "month")) {
            // The month is already fetched by the preload, so move the view without
            // going through navigateToMonth, which would clear the expansion we just
            // set and re-request the same data.
            setCalendarMonth(nextMonth);
            setRangeToMonth(nextMonth);
        }
    }, [isLoading, hasFetchedAnyMonth, displayRows, calendarMonth, setRangeToMonth]);

    const selectionKey = (row: DisplayRow) =>
        `${row.date}|${row.fareGroupId}|${row.bookingTimeId}|${row.availIndex}`;

    const isRowSelected = (row: DisplayRow) =>
        selections.some((s) => selectionKey(s) === selectionKey(row));

    const fareNeedsTimeSelection = useCallback(
        (row: DisplayRow): boolean =>
            fareNeedsTimeSelectionFn(row) ||
            (fareGroupBookingTimes[row.fareGroupId]?.length ?? 0) > 1,
        [fareGroupBookingTimes],
    );

    const fareTimeKey = (row: DisplayRow) => fareTimeKeyFn(row);

    const fetchDepartureTimesForFare = useCallback(
        async (row: DisplayRow) => {
            const key = fareTimeKey(row);
            if (fareDepartureTimes[key]?.times.length > 0 || fareDepartureTimes[key]?.loading)
                return;

            setFareDepartureTimes((prev) => ({
                ...prev,
                [key]: { loading: true, times: [] },
            }));

            try {
                const ppdId = row.productPricesDetailsId.toString();
                const bookingDate = row.availability.BookingDate;
                const bs = row.priceGroup.bookingSystem?.trim().toLowerCase() ?? "";

                let timeOptions: DepartureTimeOption[] = [];

                if (bs === "ibis") {
                    const resp = await cartApi.confirmAvailability(ppdId, bookingDate);
                    timeOptions = parseDepartureTimeFields(resp.data.fields);
                } else if (bs === "respaxv2") {
                    const availByRange = row.availability as IAvailabilityByRange;
                    timeOptions = parseDepartureTimeFields(availByRange.fields ?? []);

                    if (timeOptions.length === 0) {
                        // RespaxV2 products can expose their departure times directly
                        // on the availability rows (checkAvailabilityProduct returns
                        // each booking time + its numAvailable per date) with no
                        // per-date `timeid` field to parse. Derive the day's times
                        // from those already-fetched rows before paying for a live
                        // range call.
                        timeOptions = deriveDepartureTimesFromAvailabilityRows(displayRows, {
                            fareGroupId: row.fareGroupId,
                            productPricesDetailsId: row.productPricesDetailsId,
                            bookingDate,
                        });
                    }

                    if (timeOptions.length === 0) {
                        try {
                            const refetched = await toursApi.checkAvailRange(
                                ppdId,
                                "0",
                                new Date(bookingDate),
                                1,
                            );
                            const matchingAvail = refetched.find(
                                (a) => a.BookingDate === bookingDate,
                            );
                            if (matchingAvail) {
                                timeOptions = parseDepartureTimeFields(
                                    (matchingAvail as IAvailabilityByRange).fields ?? [],
                                );
                            }
                        } catch {
                            /* fallback: no times */
                        }
                    }
                } else {
                    // Generic products with multiple departure times: the product
                    // availability endpoint (checkAvailabilityProduct) now returns
                    // every booking time and its availability per date, so derive the
                    // day's time list from the rows already fetched into displayRows
                    // instead of making extra live per-time calls. Sold-out times are
                    // dropped below.
                    const catalog = fareGroupBookingTimes[row.fareGroupId] ?? [];
                    if (catalog.length > 1) {
                        timeOptions = deriveDepartureTimesFromRows(catalog, displayRows, {
                            fareGroupId: row.fareGroupId,
                            productPricesDetailsId: row.productPricesDetailsId,
                            bookingDate,
                        });
                    }
                }

                timeOptions = filterAvailableDepartureTimes(_.uniqBy(timeOptions, (t) => t.id));

                setFareDepartureTimes((prev) => ({
                    ...prev,
                    [key]: { loading: false, times: timeOptions },
                }));

                if (timeOptions.length === 1) {
                    handleSelectTimeForFare(row, timeOptions[0]);
                }
            } catch {
                setFareDepartureTimes((prev) => ({
                    ...prev,
                    [key]: { loading: false, times: [], error: "Failed to load times" },
                }));
            }
        },
        [fareDepartureTimes, fareGroupBookingTimes, displayRows],
    );

    const handleSelectTimeForFare = (row: DisplayRow, time: DepartureTimeOption) => {
        // `row` is the collapsed representative card, whose availIndex belongs to
        // its own (earliest) booking time. Resolve the row that matches the time
        // the user actually picked so the correct availability index is booked.
        const matchRow = findTimeAvailRow(displayRows, {
            fareGroupId: row.fareGroupId,
            bookingDate: row.availability.BookingDate,
            timeId: time.id,
        });
        const availIndex = matchRow?.availIndex ?? row.availIndex;
        const availability = matchRow?.availability ?? row.availability;
        const selKey = `${row.date}|${row.fareGroupId}|${time.id}|${availIndex}`;
        if (selections.some((s) => selectionKey(s) === selKey)) {
            setSelections((prev) => prev.filter((s) => selectionKey(s) !== selKey));
        } else {
            const selectedRow: SelectedItem = {
                ...row,
                availIndex,
                availability,
                bookingTimeId: time.id,
                bookingTimeName: time.displayName,
                numAvailable: time.numAvailable,
                quantity: row.numPax,
            };
            setSelections((prev) => [...prev, selectedRow]);
            const rrp = getRRPOfAvailability(row.availability);
            const parsedRrp = parseFloat(
                String(row.priceGroup?.rrp ?? "0").replace(/[^\d.-]/g, ""),
            );
            const price = (rrp ?? parsedRrp) || 0;
            const snapshot = {
                productId: product.productId,
                name: product.name,
                supplier: product.supplier ?? null,
                categories: product.categories as any,
                tourCode: product.tourCode,
                location: product.location,
                duration: product.duration,
            };
            pushSelectItem(snapshot, row.priceGroup, price);
        }
    };

    const handleSelectRow = (row: DisplayRow) => {
        if (fareNeedsTimeSelection(row)) {
            const key = fareTimeKey(row);
            if (expandedFareKey === key) {
                setExpandedFareKey(null);
            } else {
                setExpandedFareKey(key);
                fetchDepartureTimesForFare(row);
            }
            return;
        }

        const key = selectionKey(row);
        if (selections.some((s) => selectionKey(s) === key)) {
            setSelections((prev) => prev.filter((s) => selectionKey(s) !== key));
        } else {
            setSelections((prev) => [...prev, { ...row, quantity: row.numPax }]);
            const rrp = getRRPOfAvailability(row.availability);
            const parsedRrp = parseFloat(
                String(row.priceGroup?.rrp ?? "0").replace(/[^\d.-]/g, ""),
            );
            const price = (rrp ?? parsedRrp) || 0;
            const snapshot = {
                productId: product.productId,
                name: product.name,
                supplier: product.supplier ?? null,
                categories: product.categories as any,
                tourCode: product.tourCode,
                location: product.location,
                duration: product.duration,
            };
            pushSelectItem(snapshot, row.priceGroup, price);
        }
    };

    const buildCombinedAddItemData = (items: SelectedItem[], overrideIndices?: number[]) => {
        const productPricesDetails: IProductPricesDetails[] = items.map((item) => {
            const ppdId = item.productPricesDetailsId?.toString() ?? "";
            return {
                productPricesDetailsId: ppdId || null,
                quantityDetails: [
                    {
                        quantity: item.quantity,
                        bookingDate: item.availability.BookingDate,
                        timeId: item.bookingTimeId,
                    },
                ],
            };
        });

        const firstItem = items[0];
        return {
            tdmsProductId: product.productId,
            productPricesDetails,
            startDate: moment(firstItem.date, "DD-MMM-YYYY").format("DD-MMM-YYYY"),
            days: resolveBookingDays(product.durationDays),
            selectedAvailableIndices: overrideIndices ?? items.map((item) => item.availIndex),
        };
    };

    // Re-bases each selection's `selectedAvailableIndices` to be relative to the
    // payload's `startDate`, using only the already-loaded checkAvailabilityProduct
    // rows (`displayRows`) — no extra network call. The calendar's raw `availIndex`
    // is the row's position in the whole loaded window, but the FT backend
    // re-derives availability anchored at `startDate` and reads the row at that
    // index; sending the window-relative index makes it read the wrong row and
    // reject with "fare option no longer available". For a single-date booking
    // (startDate === bookingDate) the anchored index is 0. Falls back to the raw
    // availIndex per-item if no matching row is found.
    const resolveSelectedIndices = (items: SelectedItem[]): number[] => {
        const startDate = items[0]?.date ?? "";
        return items.map((item) => {
            const fareRows = displayRows
                .filter((r) => r.fareGroupId === item.fareGroupId)
                .map((r) => ({ date: r.date, availIndex: r.availIndex }));
            const idx = anchorIndexToStartDate(fareRows, startDate, item.availIndex);
            return idx >= 0 ? idx : item.availIndex;
        });
    };

    const executeAdd = useCallback(
        async (mode: "addToCart" | "bookNow") => {
            if (selections.length === 0) return;

            try {
                setIsSubmitInProgress(mode === "addToCart" ? "cart" : "book-now");

                // The sign-in sheet replays this action from inside its own login
                // handler, in the same tick as the JWT is stored — before React
                // re-renders this component. So the closed-over isLoggedIn is still
                // false, and the item would be added to a guest session the now
                // signed-in app never reads. The stored token is authoritative and
                // updates synchronously, so check that too.
                const signedIn = isLoggedIn || !_.isNil(lsJwt.getItem()?.accessToken);

                if (mode === "bookNow") {
                    // Book Now checks out everything waiting, not just this
                    // product: it adds to the same session Add to Cart uses and
                    // opens the cart's Checkout step, so one order covers the lot.
                    // Previously it ran on its own session that was wiped on every
                    // click, which is why anything already in the cart vanished.
                    const sessionId = signedIn ? undefined : sessionHelper.getSessionId();
                    console.info("[BookNow] sessionId:", sessionId, "signedIn:", signedIn);

                    const bnIndices = resolveSelectedIndices(selections);
                    const combinedData = buildCombinedAddItemData(selections, bnIndices);
                    console.info("[BookNow] addCart payload:", JSON.stringify(combinedData));

                    try {
                        await dispatch(
                            addToCartAndFetch({ item: combinedData, sessionId }),
                        ).unwrap();
                    } catch (addError: unknown) {
                        // Book Now on something already in the cart is not a
                        // failure: the intent is to check this product out and it
                        // is already there. Anything else still surfaces.
                        if (!isDuplicateCartItemError(addError)) throw addError;
                    }
                    setSelections([]);
                    setExpandedDate(null);
                    setCartHasItems(true);

                    // ?step= seeds useStepPersistence, which allows the Checkout
                    // step because the items already carry booking_data.
                    navigate(
                        sessionId
                            ? `${PATH_CART}?sessionId=${sessionId}&step=1`
                            : `${PATH_CART}?step=1`,
                    );
                } else {
                    const sessionId = signedIn ? undefined : sessionHelper.getSessionId();
                    console.info("[AddToCart] sessionId:", sessionId, "signedIn:", signedIn);
                    const addIndices = resolveSelectedIndices(selections);
                    const combinedData = buildCombinedAddItemData(selections, addIndices);
                    console.info(
                        "[AddToCart] anchoredIndices:",
                        JSON.stringify(addIndices),
                        "rawAvailIndices:",
                        JSON.stringify(selections.map((s) => s.availIndex)),
                        "payload:",
                        JSON.stringify(combinedData),
                    );
                    await dispatch(addToCartAndFetch({ item: combinedData, sessionId })).unwrap();
                    // Kept so the sign-up can put it on the new account if the
                    // backend's session convert drops it (see guestCartStash).
                    if (!signedIn) stashGuestCartItem(combinedData);
                    const itemCount = selections.reduce((sum, s) => sum + s.quantity, 0);
                    SuccessToast({
                        Message: `${itemCount} ${itemCount === 1 ? "item" : "items"} added to cart`,
                    });
                    setSelections([]);
                    setExpandedDate(null);
                    setCartHasItems(true);
                    setShowAddedToCart(true);
                }
            } catch (error: any) {
                console.error(
                    "[executeAdd] FT backend rejected add:",
                    "status:",
                    error?.response?.status,
                    "body:",
                    JSON.stringify(error?.response?.data),
                );
                console.error(
                    "[executeAdd] payload that failed:",
                    JSON.stringify(buildCombinedAddItemData(selections)),
                );
                ErrorToast({
                    Message: error?.response?.data?.title || error?.message || "Failed to add item",
                });
            } finally {
                setIsSubmitInProgress(null);
            }
        },
        [selections, isLoggedIn, product, dispatch, navigate, location],
    );

    const handleAddToTrip = () => {
        if (!onAddToTrip || selections.length === 0) return;
        const invalidSelection = selections.find((s) => {
            const step = s.numPax || 1;
            return s.quantity < step || s.quantity % step !== 0 || s.quantity > s.numAvailable;
        });
        if (invalidSelection) {
            const step = invalidSelection.numPax || 1;
            ErrorToast({
                Message: `Quantity for "${invalidSelection.fareGroupName}" must be a multiple of ${step} and within availability`,
            });
            return;
        }
        selections.forEach((s) => {
            const fare = s.priceGroup?.prices?.[0];
            const rrp =
                getRRPOfAvailability(s.availability) ??
                parseFloat(String(s.priceGroup?.rrp ?? "0").replace(/[^\d.-]/g, "")) ??
                0;
            onAddToTrip({
                date: s.date,
                fareTypeId: fare?.fareTypeId ?? s.fareGroupId ?? "",
                fareName: s.fareGroupName,
                productPricesDetailsId: s.productPricesDetailsId,
                bookingTimeId: s.bookingTimeId && s.bookingTimeId !== "0" ? s.bookingTimeId : null,
                bookingTimeName: s.bookingTimeName,
                quantity: s.quantity,
                numPax: s.numPax,
                rrp: Number(rrp) || 0,
                availIndex: s.availIndex,
            });
        });
        SuccessToast({
            Message: `${selections.length} ${selections.length === 1 ? "item" : "items"} added to your trip`,
        });
        setSelections([]);
        setExpandedDate(null);
    };

    const handleAdd = (mode: "addToCart" | "bookNow") => {
        const invalidSelection = selections.find((s) => {
            const step = s.numPax || 1;
            return s.quantity < step || s.quantity % step !== 0 || s.quantity > s.numAvailable;
        });
        if (invalidSelection) {
            const step = invalidSelection.numPax || 1;
            ErrorToast({
                Message: `Quantity for "${invalidSelection.fareGroupName}" must be a multiple of ${step} and within availability`,
            });
            return;
        }
        if (
            shouldPromptSignup(mode === "bookNow" ? "bookNow" : "addToCart", {
                isLoggedIn,
                promptSeen: !!sessionStorage.getItem(SIGN_IN_PROMPT_SEEN_KEY),
            })
        ) {
            pendingActionRef.current = { mode };
            // Apple and Google sign-in leave the site entirely, so the ref above
            // does not survive. The payload and the intent are parked where the
            // callback handler can pick them up on the way back.
            storePendingSocialAction({
                mode,
                item: buildCombinedAddItemData(selections, resolveSelectedIndices(selections)),
                returnPath: window.location.pathname + window.location.search,
            });
            setShowSignInPrompt(true);
            return;
        }
        // GTM add_to_cart: fire on both Add to Cart and Book Now
        if (selections.length > 0) {
            const value = selections.reduce(
                (sum, s) => sum + (getRRPOfAvailability(s.availability) ?? 0) * s.quantity,
                0,
            );
            const travelDate = moment(selections[0].date, "DD-MMM-YYYY").format("YYYY-MM-DD");
            const snapshot = {
                productId: product.productId,
                name: product.name,
                supplier: product.supplier ?? null,
                categories: product.categories as any,
                tourCode: product.tourCode,
                location: product.location,
                duration: product.duration,
            };
            pushAddToCart(snapshot, {
                value,
                travelDate,
                ctaClick: mode === "bookNow" ? "Book Now" : "Add to Cart",
            });
        }
        executeAdd(mode);
    };

    /**
     * A guest's cart hangs off a session id, which has to be carried in the URL
     * or the cart page loads empty. Same rule the View Cart bar uses.
     */
    const getAddedToCartUrl = () => {
        const sessionId = isLoggedIn ? undefined : sessionHelper.getSessionId();
        return sessionId ? `${PATH_CART}?sessionId=${sessionId}` : PATH_CART;
    };

    // Coming back from a social sign-in that completed an Add to Cart: show the
    // same "go to cart or keep shopping" prompt the visitor would have seen had
    // they never left the page. The add itself was done by the callback handler.
    useEffect(() => {
        if (consumeAddedToCartAfterSignIn()) {
            setCartHasItems(true);
            setShowAddedToCart(true);
        }
    }, []);

    const handleSignInSuccess = () => {
        sessionStorage.setItem(SIGN_IN_PROMPT_SEEN_KEY, "true");
        setShowSignInPrompt(false);
        // Resumed here and now, so the parked copy would only add the item twice.
        clearPendingSocialAction();
        const pending = pendingActionRef.current;
        pendingActionRef.current = null;
        if (pending) executeAdd(pending.mode);
    };

    const handleSaveToNewQuote = async () => {
        if (selections.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        setIsQuoteDropdownOpen(false);
        try {
            setIsSavingToQuote(true);
            const combinedData = buildCombinedAddItemData(selections);
            const title = await createNewQuoteModal();
            if (!title) {
                setIsSavingToQuote(false);
                return;
            }
            await dispatch(
                createNewQuoteAndReload({ ...combinedData, quoteTitle: title }),
            ).unwrap();
            SuccessToast({ Message: "Saved to quote" });
            setSelections([]);
            setExpandedDate(null);
            navigate(PATH_QUOTES);
        } catch (error: any) {
            console.error("Error saving to quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to save to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const handleSaveToExistingQuote = async () => {
        if (selections.length === 0) {
            ErrorToast({ Message: "No items to save" });
            return;
        }
        setIsQuoteDropdownOpen(false);
        try {
            setIsSavingToQuote(true);
            await dispatch(loadQuotes());
            const selectedQuote = await addToExistingQuoteModal();
            if (!selectedQuote) {
                ErrorToast({ Message: "No quote selected." });
                setIsSavingToQuote(false);
                return;
            }
            const combinedData = buildCombinedAddItemData(selections);
            await dispatch(
                addItemToExistingQuoteAndReload({
                    ...combinedData,
                    quoteId: selectedQuote.id,
                }),
            ).unwrap();
            SuccessToast({ Message: "Added to existing quote" });
            setSelections([]);
            setExpandedDate(null);
            navigate(`${PATH_QUOTES}/${selectedQuote.id}`);
        } catch (error: any) {
            console.error("Error adding to existing quote:", error);
            ErrorToast({ Message: error?.title || error?.message || "Failed to add to quote" });
        } finally {
            setIsSavingToQuote(false);
        }
    };

    const increaseQuantity = (key: string) => {
        setSelections((prev) =>
            prev.map((s) => {
                if (selectionKey(s) !== key) return s;
                const step = s.numPax || 1;
                if (s.quantity + step > s.numAvailable) {
                    ErrorToast({ Message: "Maximum available quantity reached" });
                    return s;
                }
                return { ...s, quantity: s.quantity + step };
            }),
        );
    };

    const decreaseQuantity = (key: string) => {
        setSelections((prev) =>
            prev.map((s) => {
                if (selectionKey(s) !== key) return s;
                const step = s.numPax || 1;
                if (s.quantity - step < step) {
                    ErrorToast({ Message: `Minimum quantity is ${step}` });
                    return s;
                }
                return { ...s, quantity: s.quantity - step };
            }),
        );
    };

    const removeSelection = (key: string) => {
        setSelections((prev) => prev.filter((s) => selectionKey(s) !== key));
    };

    const clearAvailabilityState = () => {
        setDisplayRows([]);
        setBookedOutDates(new Set());
        setExpandedDate(null);
        fetchedMonthsRef.current.clear();
        syncFetchedMonthsState();
    };

    const navigateToMonth = (target: moment.Moment) => {
        setCalendarMonth(target);
        setExpandedDate(null);
        setRangeToMonth(target);
        onCheckAvailabilityClick?.();
        runFetchForMonth(target, {
            rangeStart: target.clone().startOf("month"),
            rangeEnd: target.clone().endOf("month"),
        });
    };

    const goToPrevMonth = () => {
        const prev = calendarMonth.clone().subtract(1, "month");
        if (prev.isBefore(moment().startOf("month"))) return;
        navigateToMonth(prev);
    };

    const maxMonth = moment().startOf("month").add(12, "months");
    const isNextDisabled = calendarMonth.clone().add(1, "month").isAfter(maxMonth);

    const goToNextMonth = () => {
        const next = calendarMonth.clone().add(1, "month");
        if (next.isAfter(maxMonth)) return;
        navigateToMonth(next);
    };

    const goToMonth = (monthMoment: moment.Moment) => {
        navigateToMonth(monthMoment);
    };

    const monthPickerOptions = useMemo(() => {
        const options: { value: string; label: string }[] = [];
        const start = moment().startOf("month");
        for (let i = 0; i <= 12; i++) {
            const m = start.clone().add(i, "months");
            options.push({ value: m.format("YYYY-MM"), label: m.format("MMMM YYYY") });
        }
        return options;
    }, []);

    const handleDateCellClick = (dateKey: string, hasAvail: boolean) => {
        if (hasAvail) {
            const isOpening = expandedDate !== dateKey;
            setExpandedFareKey(null);
            setExpandedDate(isOpening ? dateKey : null);
            // Only on a deliberate pick: the load-time default also sets
            // expandedDate, and scrolling then would fight the page's own anchor.
            shouldScrollToFaresRef.current = isOpening;
        }
    };

    const handleResetRange = () => {
        setSelections([]);
        clearAvailabilityState();
        setRangeToMonth(calendarMonth);
    };

    const isPrevDisabled = calendarMonth.isSameOrBefore(moment().startOf("month"));

    const calendarDays = (() => {
        const startOfMonth = calendarMonth.clone().startOf("month");
        const endOfMonth = calendarMonth.clone().endOf("month");
        const startOfWeek = startOfMonth.clone().startOf("week");
        const endOfWeek = endOfMonth.clone().endOf("week");

        const days: moment.Moment[] = [];
        const current = startOfWeek.clone();
        while (current.isSameOrBefore(endOfWeek)) {
            days.push(current.clone());
            current.add(1, "day");
        }
        return days;
    })();

    const today = moment().startOf("day");
    const monthKey = calendarMonth.format("YYYY-MM");
    const isCurrentMonthFetched = fetchedMonthsSet.has(monthKey);

    const expandedRows = expandedDate
        ? (byDateNormalised[expandedDate] || []).slice().sort((a, b) => {
              const aDefault = a.priceGroup.prices.some((p) => p.defaultFare) ? 0 : 1;
              const bDefault = b.priceGroup.prices.some((p) => p.defaultFare) ? 0 : 1;
              if (aDefault !== bDefault) return aDefault - bDefault;
              const aTime = parseBookingTime(a.bookingTimeName);
              const bTime = parseBookingTime(b.bookingTimeName);
              if (aTime !== bTime) return aTime - bTime;
              return a.fareGroupName.localeCompare(b.fareGroupName);
          })
        : [];

    // Multi-time fares arrive as one availability row per booking time, so the
    // same fare would otherwise render once per time (e.g. ADULT shown 6×).
    // Collapse those down to one card per fare; the times live in the accordion.
    const visibleFareRows = collapseTimedFareRows(expandedRows, fareNeedsTimeSelection);

    // The visitor sees one wait, not two: the gap before the first request and the
    // request itself.
    const isCalendarLoading = isLoading || isAwaitingFirstFetch;

    const calendarContent = (
        <CalendarContainer>
            <DateRangeRow>
                <DateRangeTopRow>
                    <DateRangeLabel>
                        {isCurrentMonthFetched ? "Showing availability" : "Select a month"}
                    </DateRangeLabel>
                    {(hasFetchedAnyMonth || isCurrentMonthFetched) && (
                        <ResetButton type="button" onClick={handleResetRange}>
                            Start over
                        </ResetButton>
                    )}
                </DateRangeTopRow>
                {rangeStart && rangeEnd && (
                    <SelectedRangeDisplay>
                        {moment(rangeStart).format("D MMM YYYY")} –{" "}
                        {moment(rangeEnd).format("D MMM YYYY")}
                    </SelectedRangeDisplay>
                )}
            </DateRangeRow>
            <CalendarHeader>
                <NavBtn onClick={goToPrevMonth} disabled={isPrevDisabled}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path
                            d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"
                            fill="currentColor"
                        />
                    </svg>
                </NavBtn>
                <MonthPickerSelect
                    value={calendarMonth.format("YYYY-MM")}
                    onChange={(e) => {
                        const selected = moment(e.target.value, "YYYY-MM").startOf("month");
                        goToMonth(selected);
                    }}
                >
                    {monthPickerOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </MonthPickerSelect>
                <NavBtn onClick={goToNextMonth} disabled={isNextDisabled}>
                    <svg viewBox="0 0 24 24" width="20" height="20">
                        <path
                            d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"
                            fill="currentColor"
                        />
                    </svg>
                </NavBtn>
            </CalendarHeader>
            <CalendarGridWrapper>
                <CalendarGrid $isLoading={isCalendarLoading}>
                    {DAY_HEADERS.map((d) => (
                        <DayHeaderCell key={d}>{d}</DayHeaderCell>
                    ))}
                    {calendarDays.map((day, i) => {
                        const dateKey = day.format("YYYY-MM-DD");
                        const isCurrentMonth = day.isSame(calendarMonth, "month");
                        const isPast = day.isBefore(today);
                        const rows =
                            isCurrentMonthFetched || isLoading
                                ? byDateNormalised[dateKey] || []
                                : [];
                        const hasAvail = rows.length > 0;
                        const isBookedOut = !hasAvail && bookedOutNormalised.has(dateKey);
                        const isExpanded = expandedDate === dateKey;
                        const isDisabled = !isCurrentMonth || isPast;
                        // Infant (FOC) and add-on fares are not bookable spaces, so
                        // they must not inflate the day's availability count.
                        const totalSpaces = rows
                            .filter((r) => !isExcludedFromSpaceCount(r.priceGroup))
                            .reduce((max, r) => Math.max(max, r.numAvailable), 0);
                        if (!isCurrentMonthFetched && !isLoading) {
                            return (
                                <DayCell
                                    key={i}
                                    $isOtherMonth={isDisabled}
                                    $hasAvail={false}
                                    $isExpanded={false}
                                >
                                    <DayNumber>{day.date()}</DayNumber>
                                </DayCell>
                            );
                        }

                        return (
                            <DayCell
                                key={i}
                                $isOtherMonth={isDisabled}
                                $hasAvail={hasAvail && !isDisabled}
                                $isExpanded={isExpanded}
                                $isBookedOut={isBookedOut && !isDisabled}
                                onClick={() =>
                                    !isDisabled && handleDateCellClick(dateKey, hasAvail)
                                }
                            >
                                <DayNumber>{day.date()}</DayNumber>
                                {hasAvail && !isDisabled && totalSpaces > 0 && (
                                    <AvailDot $availLevel={getAvailabilityLevel(totalSpaces)}>
                                        {formatAvailableSpaces(totalSpaces)}
                                    </AvailDot>
                                )}
                            </DayCell>
                        );
                    })}
                </CalendarGrid>
                {isCalendarLoading && (
                    <CalendarLoadingOverlay>
                        <ClipLoaderCommon color={theme.colors.highlight} size={36} margin={0} />
                    </CalendarLoadingOverlay>
                )}
            </CalendarGridWrapper>

            {!isLoading &&
                isCurrentMonthFetched &&
                Object.keys(byDateNormalised).filter((k) =>
                    moment(k).isSame(calendarMonth, "month"),
                ).length === 0 && (
                    <EmptyMessage>No availability this month. Try another month.</EmptyMessage>
                )}

            <CalendarLegend>
                <Tooltip
                    title="More than 10 spaces available"
                    arrow
                    enterDelay={200}
                    enterTouchDelay={0}
                >
                    <LegendItem>
                        <LegendColorDot color={AVAILABILITY_COLORS.plenty} /> Plenty of spots
                    </LegendItem>
                </Tooltip>
                <Tooltip
                    title="Between 4 and 10 spaces remaining"
                    arrow
                    enterDelay={200}
                    enterTouchDelay={0}
                >
                    <LegendItem>
                        <LegendColorDot color={AVAILABILITY_COLORS.limited} /> Filling fast
                    </LegendItem>
                </Tooltip>
                <Tooltip title="3 or fewer spaces left" arrow enterDelay={200} enterTouchDelay={0}>
                    <LegendItem>
                        <LegendColorDot color={AVAILABILITY_COLORS.scarce} /> Almost full
                    </LegendItem>
                </Tooltip>
            </CalendarLegend>
        </CalendarContainer>
    );

    const fareDetailContent =
        expandedDate && expandedRows.length > 0 ? (
            <FareDetailPanel ref={fareDetailRef}>
                <FareDetailHeader>
                    {moment(expandedDate).format("dddd, D MMMM YYYY")}
                    <CloseBtn
                        onClick={() => {
                            setExpandedDate(null);
                            setExpandedFareKey(null);
                        }}
                    >
                        &times;
                    </CloseBtn>
                </FareDetailHeader>
                {visibleFareRows.map((row, i) => {
                    const needsTime = fareNeedsTimeSelection(row);
                    const ftKey = fareTimeKey(row);
                    const isFareExpanded = expandedFareKey === ftKey;
                    const fareTimesData = fareDepartureTimes[ftKey];
                    const hasSelectedTimesForFare =
                        needsTime &&
                        selections.some(
                            (s) => s.date === row.date && s.fareGroupId === row.fareGroupId,
                        );
                    const rowSelected = needsTime ? hasSelectedTimesForFare : isRowSelected(row);
                    const rowPrice = getRRPOfAvailability(row.availability);
                    const priceDisplay = rowPrice !== null ? `$${rowPrice.toFixed(2)}` : null;

                    return (
                        <div key={`${row.fareGroupId}-${row.bookingTimeId}-${i}`}>
                            <FareRow
                                $isSelected={rowSelected}
                                onClick={() => handleSelectRow(row)}
                                data-gtm-id="select-item-fare"
                            >
                                {!needsTime && (
                                    <FareCheckbox $isSelected={rowSelected}>
                                        {rowSelected && <span>&#10003;</span>}
                                    </FareCheckbox>
                                )}
                                {needsTime && (
                                    <FareExpandIcon $isExpanded={isFareExpanded}>
                                        &#9654;
                                    </FareExpandIcon>
                                )}
                                <FareInfo>
                                    <FareName>{row.fareGroupName}</FareName>
                                    {row.priceGroup.travelTimeMessage && (
                                        <TravelDateLabel>
                                            {row.priceGroup.travelTimeMessage}
                                        </TravelDateLabel>
                                    )}
                                    {!needsTime && row.bookingTimeName && (
                                        <TimeLabel>{row.bookingTimeName}</TimeLabel>
                                    )}
                                    {needsTime && !isFareExpanded && !fareTimesData?.loading && (
                                        <TimeLabel>Click to see departure times</TimeLabel>
                                    )}
                                </FareInfo>
                                {!needsTime && (
                                    <AvailCount
                                        $availLevel={getAvailabilityLevel(row.numAvailable)}
                                    >
                                        {formatAvailableSpacesLabel(row.numAvailable)}
                                    </AvailCount>
                                )}
                                <FarePrice $isSelected={rowSelected}>
                                    {priceDisplay ?? "\u2014"}
                                </FarePrice>
                            </FareRow>
                            {needsTime && isFareExpanded && (
                                <DepartureTimesPanel>
                                    {fareTimesData?.loading && (
                                        <DepartureTimesLoading>
                                            <ClipLoaderCommon
                                                color={theme.colors.main}
                                                size={16}
                                                margin={0}
                                            />
                                            <span>Loading departure times...</span>
                                        </DepartureTimesLoading>
                                    )}
                                    {fareTimesData?.error && (
                                        <DepartureTimesError>
                                            {fareTimesData.error}
                                        </DepartureTimesError>
                                    )}
                                    {fareTimesData &&
                                        !fareTimesData.loading &&
                                        fareTimesData.times.length === 0 &&
                                        !fareTimesData.error && (
                                            <DepartureTimesError>
                                                No departure times available
                                            </DepartureTimesError>
                                        )}
                                    {fareTimesData &&
                                        !fareTimesData.loading &&
                                        fareTimesData.times.length > 0 && (
                                            <DepartureTimesGrid>
                                                {fareTimesData.times.map((time) => {
                                                    const isTimeSelected = selections.some(
                                                        (s) =>
                                                            s.date === row.date &&
                                                            s.fareGroupId === row.fareGroupId &&
                                                            s.bookingTimeId === time.id,
                                                    );
                                                    const availLevel = getAvailabilityLevel(
                                                        time.numAvailable,
                                                    );
                                                    return (
                                                        <DepartureTimeButton
                                                            key={time.id}
                                                            $isSelected={isTimeSelected}
                                                            $availLevel={availLevel}
                                                            disabled={time.numAvailable <= 0}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (time.numAvailable > 0)
                                                                    handleSelectTimeForFare(
                                                                        row,
                                                                        time,
                                                                    );
                                                            }}
                                                            data-gtm-id="select-item-departure-time"
                                                        >
                                                            <DepartureTimeName>
                                                                {time.displayName}
                                                            </DepartureTimeName>
                                                            <DepartureTimeAvail
                                                                $availLevel={availLevel}
                                                            >
                                                                {time.numAvailable > 0
                                                                    ? formatAvailableSpacesLabel(
                                                                          time.numAvailable,
                                                                      )
                                                                    : "Sold out"}
                                                            </DepartureTimeAvail>
                                                            {isTimeSelected && (
                                                                <DepartureTimeCheck>
                                                                    &#10003;
                                                                </DepartureTimeCheck>
                                                            )}
                                                        </DepartureTimeButton>
                                                    );
                                                })}
                                            </DepartureTimesGrid>
                                        )}
                                </DepartureTimesPanel>
                            )}
                        </div>
                    );
                })}
            </FareDetailPanel>
        ) : null;

    return (
        <>
            <PanelWrapper $layout={layout}>
                {calendarContent}
                {fareDetailContent}
            </PanelWrapper>

            {selections.length > 0 && (
                <SelectionBar>
                    <SelectionItemsList>
                        {selections.map((item) => {
                            const key = selectionKey(item);
                            return (
                                <SelectionItem key={key}>
                                    <SelectionItemInfo>
                                        <SelectionFare>{item.fareGroupName}</SelectionFare>
                                        <SelectionMeta>
                                            {moment(item.date, "DD-MMM-YYYY").format("D MMM")}
                                            {item.bookingTimeName && ` · ${item.bookingTimeName}`}
                                        </SelectionMeta>
                                    </SelectionItemInfo>
                                    <QuantityControl>
                                        <QtyButton onClick={() => decreaseQuantity(key)}>
                                            −
                                        </QtyButton>
                                        <QtyValue>{item.quantity}</QtyValue>
                                        <QtyButton onClick={() => increaseQuantity(key)}>
                                            +
                                        </QtyButton>
                                    </QuantityControl>
                                    <RemoveBtn onClick={() => removeSelection(key)}>
                                        &times;
                                    </RemoveBtn>
                                </SelectionItem>
                            );
                        })}
                    </SelectionItemsList>
                    <ActionButtons>
                        {actionMode === "trip" ? (
                            <CartButton
                                onClick={handleAddToTrip}
                                disabled={selections.length === 0}
                                data-gtm-id="add-to-trip"
                            >
                                {`Add ${selections.length > 1 ? `${selections.length} items` : ""} to Trip`}
                            </CartButton>
                        ) : (
                            <>
                                <CartButton
                                    onClick={() => handleAdd("addToCart")}
                                    disabled={isSubmitInProgress !== null || isSavingToQuote}
                                    data-gtm-id="add-to-cart"
                                >
                                    {isSubmitInProgress === "cart" ? (
                                        <ClipLoaderCommon size={18} color="#fff" margin={0} />
                                    ) : (
                                        `Add ${selections.length > 1 ? `${selections.length} items` : ""} to Cart`
                                    )}
                                </CartButton>
                                <BookButton
                                    onClick={() => handleAdd("bookNow")}
                                    disabled={isSubmitInProgress !== null || isSavingToQuote}
                                    $isBusy={isSubmitInProgress === "book-now"}
                                    data-gtm-id="book-now"
                                >
                                    {isSubmitInProgress === "book-now" ? (
                                        <ClipLoaderCommon size={18} color="#fff" margin={0} />
                                    ) : (
                                        "Book Now"
                                    )}
                                </BookButton>
                                {showSaveToQuote && (
                                    <QuoteButtonWrapper>
                                        <QuoteButton
                                            onClick={handleSaveToNewQuote}
                                            disabled={
                                                isSubmitInProgress !== null || isSavingToQuote
                                            }
                                            data-gtm-id="save-to-quote"
                                        >
                                            {isSavingToQuote ? (
                                                <ClipLoaderCommon
                                                    size={18}
                                                    color={theme.colors.highlight}
                                                    margin={0}
                                                />
                                            ) : (
                                                "Save to Quote"
                                            )}
                                        </QuoteButton>
                                        <QuoteChevronButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setIsQuoteDropdownOpen(!isQuoteDropdownOpen);
                                            }}
                                            disabled={
                                                isSubmitInProgress !== null || isSavingToQuote
                                            }
                                        >
                                            <img
                                                src="/images/chevron-up.svg"
                                                alt="chevron"
                                                style={{
                                                    width: 12,
                                                    height: 12,
                                                    transition: "transform 0.2s",
                                                    transform: isQuoteDropdownOpen
                                                        ? "rotate(180deg)"
                                                        : "rotate(0deg)",
                                                    filter: `brightness(0) saturate(100%) invert(73%) sepia(74%) saturate(1000%) hue-rotate(3deg) brightness(104%) contrast(104%)`,
                                                }}
                                            />
                                        </QuoteChevronButton>
                                        {isQuoteDropdownOpen && !isSavingToQuote && (
                                            <QuoteDropdownMenu>
                                                <QuoteDropdownItem
                                                    onClick={handleSaveToExistingQuote}
                                                    data-gtm-id="add-to-existing-quote"
                                                >
                                                    Add to existing quote
                                                </QuoteDropdownItem>
                                                <QuoteDropdownItem
                                                    onClick={handleSaveToNewQuote}
                                                    data-gtm-id="save-as-new-quote"
                                                >
                                                    Save as new quote
                                                </QuoteDropdownItem>
                                            </QuoteDropdownMenu>
                                        )}
                                    </QuoteButtonWrapper>
                                )}
                            </>
                        )}
                    </ActionButtons>
                </SelectionBar>
            )}

            {cartHasItems && selections.length === 0 && (
                <ViewCartBar>
                    <ViewCartText>
                        Items added to your cart
                        {priceGroups.length > 3 &&
                            " — select another fare above to add more, or checkout now"}
                    </ViewCartText>
                    <ViewCartButton
                        onClick={() => {
                            const sessionId = isLoggedIn ? undefined : sessionHelper.getSessionId();
                            navigate(sessionId ? `${PATH_CART}?sessionId=${sessionId}` : PATH_CART);
                        }}
                    >
                        View Cart & Checkout
                    </ViewCartButton>
                </ViewCartBar>
            )}

            {/* Book Now leaves this page once the item is added, and the add plus
                the availability re-check behind it take a few seconds. Without
                this the page stays fully live under a spinner, so a second fare
                can be picked or the date changed for a booking already on its
                way. Add to Cart is left alone: it stays here, so blanking the
                page for it would just flash. */}
            {isSubmitInProgress === "book-now" && <SubmitBlockingOverlay />}

            <RedirectToCart
                open={showAddedToCart}
                onClose={() => setShowAddedToCart(false)}
                cartUrl={getAddedToCartUrl()}
            />

            {showSignInPrompt && (
                <SignInBottomSheetModal
                    isOpen={showSignInPrompt}
                    onClose={() => {
                        setShowSignInPrompt(false);
                        pendingActionRef.current = null;
                        clearPendingSocialAction();
                    }}
                    sessionId={sessionHelper.getSessionId()}
                    onLoginSuccess={handleSignInSuccess}
                />
            )}
        </>
    );
};

const PanelWrapper = styled.div<{ $layout?: "page" | "modal" }>`
    margin-top: ${(p) => (p.$layout === "modal" ? "0" : "24px")};
    border-top: ${(p) => (p.$layout === "modal" ? "none" : "2px solid #f0f0f0")};
    padding-top: ${(p) => (p.$layout === "modal" ? "0" : "16px")};
    display: flex;
    gap: 20px;
    align-items: flex-start;

    ${(p) =>
        p.$layout !== "modal" &&
        `@media (min-width: 768px) {
            width: 90vw;
            max-width: 1100px;
            position: relative;
            left: 50%;
            transform: translateX(-50%);
        }`}

    @media (max-width: 1199px) {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }
`;

const CalendarContainer = styled.div`
    background: #fff;
    border-radius: 12px;
    border: 1px solid #e8e8e8;
    padding: 20px;
    width: 420px;
    min-width: 320px;
    flex-shrink: 0;

    @media (max-width: 767px) {
        padding: 12px;
        width: 100%;
        min-width: unset;
    }
`;

const DateRangeRow = styled.div`
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f0f0;
`;

const DateRangeTopRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const DateRangeLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #555;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const ResetButton = styled.button`
    background: ${theme.colors.main};
    border: 1px solid ${theme.colors.main};
    border-radius: 6px;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    /* Dark label: white on yellow is not readable. */
    color: ${theme.colors.black};
    cursor: pointer;
    transition: all 0.15s;
    font-family: inherit;

    &:hover {
        opacity: 0.85;
    }
`;

const SelectedRangeDisplay = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${theme.colors.black};
    /* The brand yellow at low opacity, so the tint tracks the flavour instead of
       being a second hard-coded colour to keep in step. */
    padding: 8px 12px;
    background: ${theme.colors.main}26;
    border-radius: 8px;
    text-align: center;
`;

const CalendarHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const MonthPickerSelect = styled.select`
    font-weight: 700;
    font-size: 16px;
    color: #111;
    background: transparent;
    border: none;
    cursor: pointer;
    text-align: center;
    padding: 4px 8px;
    border-radius: 4px;
    appearance: none;
    -webkit-appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23666' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 4px center;
    padding-right: 20px;

    &:hover {
        background-color: #f0f0f0;
    }

    &:focus {
        outline: none;
        background-color: #f0f0f0;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const NavBtn = styled.button<{ disabled?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid #ddd;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
    color: ${(p) => (p.disabled ? "#ccc" : "#333")};
    opacity: ${(p) => (p.disabled ? 0.4 : 1)};

    &:hover:not(:disabled) {
        background: #f5f5f5;
    }
`;

/* Anchors the loading overlay to the grid alone, so the month navigation above
   it stays usable while a month is being fetched. */
const CalendarGridWrapper = styled.div`
    position: relative;
`;

const CalendarGrid = styled.div<{ $isLoading?: boolean }>`
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 2px;
    /* Greyed back rather than hidden: the month's shape stays on screen, so the
       panel does not collapse and re-expand on every month change. */
    opacity: ${(p) => (p.$isLoading ? 0.4 : 1)};
    pointer-events: ${(p) => (p.$isLoading ? "none" : "auto")};
    transition: opacity 0.15s;
`;

const CalendarLoadingOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    /* The shared loader is built to fill a page on its own (flex: 1), which here
       stretches it over the whole grid instead of centring it. */
    .clip-loader {
        flex: 0 0 auto;
        margin: 0;
    }
`;

const DayHeaderCell = styled.div`
    text-align: center;
    padding: 8px 0;
    font-size: 12px;
    font-weight: 600;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const DayCell = styled.div<{
    $isOtherMonth: boolean;
    $hasAvail: boolean;
    $isExpanded: boolean;
    $isBookedOut?: boolean;
}>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 48px;
    font-size: 14px;
    border-radius: 8px;
    cursor: ${(p) => (p.$isOtherMonth ? "default" : "pointer")};
    color: ${(p) => (p.$isOtherMonth ? "#ccc" : "#333")};
    background: #fff;
    border: 2px solid ${(p) => (p.$isExpanded ? p.theme.colors.main : "transparent")};
    transition: border-color 0.15s;
    position: relative;

    &:hover {
        background: ${(p) => (p.$isOtherMonth ? "#fff" : "#f5f5f5")};
    }

    @media (max-width: 767px) {
        min-height: 40px;
        font-size: 13px;
    }
`;

const DayNumber = styled.span`
    font-weight: 500;
    line-height: 1;
`;

const AvailDot = styled.span<{ $availLevel: AvailabilityLevel }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${(p) => AVAILABILITY_COLORS[p.$availLevel]};
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    margin-top: 2px;
    line-height: 1;
    cursor: help;
`;

const EmptyMessage = styled.p`
    text-align: center;
    color: #888;
    font-size: 14px;
    padding: 16px 8px 4px;
    margin: 0;
`;

const CalendarLegend = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px 20px;
    justify-content: center;
    padding-top: 12px;
    border-top: 1px solid #f0f0f0;
    margin-top: 12px;
`;

const LegendItem = styled.span`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #666;
`;

const LegendColorDot = styled.span<{ color: string }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.color};
`;

const FareDetailPanel = styled.div`
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 12px;
    padding: 16px;
    flex: 1;
    min-width: 0;

    @media (max-width: 1199px) {
        width: 100%;
    }
`;

const FareDetailHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-weight: 700;
    font-size: 15px;
    color: #111;
    padding-bottom: 12px;
    border-bottom: 1px solid #eee;
    margin-bottom: 10px;
`;

const CloseBtn = styled.button`
    background: none;
    border: none;
    font-size: 22px;
    color: #888;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const FareRow = styled.div<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    box-sizing: border-box;
    max-width: 100%;
    padding: 12px 14px;
    border-radius: 8px;
    margin-bottom: 6px;
    cursor: pointer;
    border: 2px solid ${(p) => (p.$isSelected ? p.theme.colors.main : "#e8e8e8")};
    background: ${(p) => (p.$isSelected ? `${p.theme.colors.main}0d` : "#fff")};
    transition:
        border-color 0.15s,
        background 0.15s;

    &:hover {
        border-color: ${(p) => p.theme.colors.main};
    }
`;

const FareInfo = styled.div`
    flex: 1;
    /* Flex items default to min-width:auto, so a long fare name refuses to
       shrink and pushes the price out past the card edge on narrow screens. */
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FareName = styled.span`
    font-weight: 600;
    font-size: 14px;
    color: #222;
    /* Long unbroken fare names ("ALL INCLUSIVE: Adult") must wrap rather than
       set the row's minimum width. */
    overflow-wrap: anywhere;
`;

const TravelDateLabel = styled.span`
    font-size: 12px;
    color: #777;
`;

const TimeLabel = styled.span`
    font-size: 13px;
    color: #555;
`;

const AvailCount = styled.span<{ $availLevel: AvailabilityLevel }>`
    font-size: 13px;
    font-weight: 600;
    color: ${(p) => AVAILABILITY_COLORS[p.$availLevel]};
    margin: 0 16px;
    white-space: nowrap;
    flex-shrink: 0;

    @media (max-width: 480px) {
        margin: 0 8px;
        font-size: 12px;
    }
`;

const FarePrice = styled.span<{ $isSelected: boolean }>`
    font-size: 14px;
    font-weight: 700;
    color: ${(p) => (p.$isSelected ? p.theme.colors.main : "#333")};
    min-width: 70px;
    text-align: right;
    white-space: nowrap;
    flex-shrink: 0;

    @media (max-width: 480px) {
        min-width: 0;
    }
`;

const FareCheckbox = styled.div<{ $isSelected: boolean }>`
    width: 20px;
    height: 20px;
    border-radius: 4px;
    border: 2px solid ${(p) => (p.$isSelected ? p.theme.colors.main : "#ccc")};
    background: ${(p) => (p.$isSelected ? p.theme.colors.main : "transparent")};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    flex-shrink: 0;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
    transition: all 0.15s;
`;

const SelectionBar = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #111;
    color: #fff;
    padding: 14px 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 100;
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.2);
`;

const SelectionItemsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const SelectionItem = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background: rgba(255, 255, 255, 0.08);
    border-radius: 8px;

    @media (max-width: 767px) {
        flex-wrap: wrap;
        gap: 8px;
    }
`;

const SelectionItemInfo = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 100px;
`;

const SelectionFare = styled.span`
    font-weight: 700;
    font-size: 14px;
`;

const SelectionMeta = styled.span`
    font-size: 12px;
    color: #aaa;
`;

const RemoveBtn = styled.button`
    background: none;
    border: none;
    color: #888;
    font-size: 20px;
    cursor: pointer;
    padding: 0 4px;
    line-height: 1;
    flex-shrink: 0;

    &:hover {
        color: #ff5555;
    }
`;

const QuantityControl = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const QtyButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: 2px solid #fff;
    background: transparent;
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;

    &:hover {
        background: rgba(255, 255, 255, 0.15);
    }
`;

const QtyValue = styled.span`
    font-size: 16px;
    font-weight: 700;
    min-width: 24px;
    text-align: center;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 10px;

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const CartButton = styled.button`
    padding: 10px 20px;
    border-radius: 6px;
    border: 2px solid #fff;
    background: transparent;
    color: #fff;
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 110px;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:not(:disabled):hover {
        background: rgba(255, 255, 255, 0.1);
    }

    @media (max-width: 767px) {
        flex: 1;
    }
`;

const BookButton = styled.button<{ $isBusy?: boolean }>`
    padding: 10px 20px;
    border-radius: 6px;
    border: none;
    /* Solid grey while the add is in flight, brand colour otherwise. */
    background: ${(p) => (p.$isBusy ? p.theme.colors.gray_3 : p.theme.colors.main)};
    color: ${(p) => p.theme.colors.button_text};
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 110px;
    justify-content: center;

    /* Disabled covers both "another action is running" and "this one is", and
       those want opposite treatments. Fading the element faded the spinner inside
       it too; the busy state keeps full opacity and says so with the colour. */
    &:disabled {
        opacity: ${(p) => (p.$isBusy ? 1 : 0.6)};
        cursor: ${(p) => (p.$isBusy ? "progress" : "not-allowed")};
    }

    &:not(:disabled):hover {
        opacity: 0.9;
    }

    @media (max-width: 767px) {
        flex: 1;
    }
`;

const ViewCartBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: ${theme.colors.main}15;
    border: 1px solid ${theme.colors.main};
    border-radius: 8px;
    padding: 12px 16px;
    margin-top: 12px;

    @media (max-width: 767px) {
        flex-direction: column;
        gap: 10px;
        text-align: center;
    }
`;

const ViewCartText = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #333;
`;

const ViewCartButton = styled.button`
    background-color: ${theme.colors.main};
    /* Not a literal white: on the yellow Peterpans accent the label has to be
       black, which is what this token carries per flavor. */
    color: ${theme.colors.button_text};
    border: none;
    border-radius: 6px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        opacity: 0.9;
    }

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const QuoteButtonWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: stretch;

    @media (max-width: 767px) {
        flex: 1;
    }
`;

const QuoteButton = styled.button`
    padding: 10px 18px;
    border-radius: 6px 0 0 6px;
    border: 2px solid ${theme.colors.main};
    border-right: none;
    background: transparent;
    color: ${theme.colors.main};
    font-weight: 700;
    font-size: 13px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 90px;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:not(:disabled):hover {
        background: ${theme.colors.main}10;
    }

    @media (max-width: 767px) {
        flex: 1;
    }
`;

const QuoteChevronButton = styled.button`
    padding: 0 10px;
    border-radius: 0 6px 6px 0;
    /* Right half of the same control as QuoteButton, so its border matches or the
       outline reads as two mismatched buttons. */
    border: 2px solid ${theme.colors.main};
    border-left: 1px solid ${theme.colors.main};
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }

    &:not(:disabled):hover {
        background: ${theme.colors.main}10;
    }
`;

const QuoteDropdownMenu = styled.div`
    position: absolute;
    bottom: 100%;
    right: 0;
    margin-bottom: 4px;
    background: white;
    border: 1px solid #ddd;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 100;
    min-width: 200px;
    overflow: hidden;
`;

const QuoteDropdownItem = styled.div`
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    color: #333;

    &:hover {
        background: #f5f5f5;
    }

    &:not(:last-child) {
        border-bottom: 1px solid #f0f0f0;
    }
`;

const FareExpandIcon = styled.span<{ $isExpanded: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    margin-right: 10px;
    flex-shrink: 0;
    font-size: 11px;
    color: #888;
    transition: transform 0.2s;
    transform: rotate(${(p) => (p.$isExpanded ? "90deg" : "0deg")});
`;

const DepartureTimesPanel = styled.div`
    margin: 0 0 10px 30px;
    padding: 10px 14px;
    background: #fafafa;
    border-radius: 8px;
    border: 1px solid #e8e8e8;
`;

const DepartureTimesLoading = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    font-size: 13px;
    color: #777;
`;

const DepartureTimesError = styled.div`
    font-size: 13px;
    color: #999;
    padding: 8px 0;
`;

const DepartureTimesGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const DepartureTimeButton = styled.button<{ $isSelected: boolean; $availLevel: AvailabilityLevel }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 16px;
    border-radius: 8px;
    border: 2px solid ${(p) => (p.$isSelected ? p.theme.colors.main : "#ddd")};
    background: ${(p) => (p.$isSelected ? `${p.theme.colors.main}12` : "#fff")};
    cursor: ${(p) => (p.disabled ? "not-allowed" : "pointer")};
    opacity: ${(p) => (p.disabled ? 0.45 : 1)};
    min-width: 100px;
    transition:
        border-color 0.15s,
        background 0.15s;

    &:not(:disabled):hover {
        border-color: ${(p) => p.theme.colors.main};
    }
`;

const DepartureTimeName = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #222;
`;

const DepartureTimeAvail = styled.span<{ $availLevel: AvailabilityLevel }>`
    font-size: 12px;
    font-weight: 600;
    color: ${(p) => AVAILABILITY_COLORS[p.$availLevel]};
    margin-top: 2px;
`;

const DepartureTimeCheck = styled.span`
    position: absolute;
    top: 4px;
    right: 6px;
    font-size: 12px;
    font-weight: 700;
    color: ${theme.colors.main};
`;

const SubmitBlockingOverlay = styled.div`
    position: fixed;
    inset: 0;
    /* A raw value, not Z_INDEX: that scale tops out at 98, while this panel's own
       action bar sits at 100 and the sign-in sheet at 1000. 999 is the one band
       that covers the page and the bar while leaving the sheet and its "Signing
       Up" notice visible above, which matters during the post-sign-up replay. */
    z-index: 999;
    background: rgba(255, 255, 255, 0.55);
    cursor: progress;
`;
